import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

export interface TimeLog {
  id: number;
  timelog_id: string;
  user_id: number;
  activity_type: 'goal' | 'task' | 'habit' | 'meditation';
  activity_id: string;
  activity_name: string;
  duration_minutes: number;
  start_time: string; // ISO timestamp
  end_time: string; // ISO timestamp
  description?: string;
  is_manual_entry: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateTimeLogData {
  user_id: number;
  activity_type: 'goal' | 'task' | 'habit' | 'meditation';
  activity_id: string;
  activity_name: string;
  duration_minutes: number;
  start_time: string; // ISO timestamp
  end_time: string; // ISO timestamp
  description?: string;
  is_manual_entry?: boolean;
}

export interface UpdateTimeLogData {
  activity_type?: 'goal' | 'task' | 'habit' | 'meditation';
  activity_id?: string;
  activity_name?: string;
  duration_minutes?: number;
  start_time?: string;
  end_time?: string;
  description?: string;
  is_manual_entry?: boolean;
}

export interface GetUserTimeLogsParams {
  start_date?: string; // YYYY-MM-DD format
  end_date?: string; // YYYY-MM-DD format
  activity_type?: 'goal' | 'task' | 'habit' | 'meditation';
  activity_id?: string;
  page?: number;
  limit?: number;
  is_manual_entry?: boolean;
}

export interface GetTimeLogStatsParams {
  start_date?: string; // YYYY-MM-DD format
  end_date?: string; // YYYY-MM-DD format
  activity_type?: 'goal' | 'task' | 'habit' | 'meditation';
  group_by?: 'activity_type' | 'activity_id' | 'date' | 'week' | 'month';
}

export interface GetDailySummaryParams {
  start_date?: string; // YYYY-MM-DD format
  end_date?: string; // YYYY-MM-DD format
  limit?: number;
}

export interface PaginationResponse {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
  has_next_page: boolean;
  has_prev_page: boolean;
}

export interface TimeLogsResponse {
  success: boolean;
  data: TimeLog[];
  pagination?: PaginationResponse;
  date?: string;
  count?: number;
  total_minutes?: number;
  total_hours?: number;
}

export interface SingleTimeLogResponse {
  success: boolean;
  data: TimeLog;
  message?: string;
}

export interface TimeLogStatsResponse {
  success: boolean;
  data: TimeLogStat[];
  group_by: string;
  count: number;
}

export interface TimeLogStat {
  user_id: number;
  activity_type: 'goal' | 'task' | 'habit' | 'meditation';
  activity_id: string;
  activity_name: string;
  total_sessions: number;
  total_minutes: number;
  avg_duration_minutes: number;
  first_logged: string;
  last_logged: string;
  log_date?: string;
  log_year?: number;
  log_week?: number;
  log_month?: number;
}

export interface DailySummaryResponse {
  success: boolean;
  data: DailySummary[];
  count: number;
}

export interface DailySummary {
  user_id: number;
  log_date: string;
  total_sessions: number;
  total_minutes: number;
  activity_types_count: number;
  unique_activities_count: number;
  activity_types: string;
  activities: string;
}

export interface ActivityTimeLogsResponse {
  success: boolean;
  data: TimeLog[];
  activity_type: string;
  activity_id: string;
  count: number;
  total_minutes: number;
  total_hours: number;
}

export class TimeLogsService {
  /**
   * Create a new time log entry
   */
  static async createTimeLog(timeLogData: CreateTimeLogData): Promise<TimeLog> {
    const response = await makeAuthenticatedRequest('/timelogs', {
      method: 'POST',
      body: JSON.stringify(timeLogData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create time log');
    }

    return response.data;
  }

  /**
   * Get a time log by ID
   */
  static async getTimeLogById(timelogId: string): Promise<TimeLog> {
    const response = await makeAuthenticatedRequest(`/timelogs/${timelogId}`);

    if (!response.success) {
      throw new Error('Failed to fetch time log');
    }

    return response.data;
  }

  /**
   * Get user's time logs with optional filtering and pagination
   */
  static async getUserTimeLogs(userId: number, params: GetUserTimeLogsParams = {}): Promise<TimeLogsResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.activity_type) queryParams.append('activity_type', params.activity_type);
    if (params.activity_id) queryParams.append('activity_id', params.activity_id);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.is_manual_entry !== undefined) queryParams.append('is_manual_entry', params.is_manual_entry.toString());

    const url = `/timelogs/user/${userId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await makeAuthenticatedRequest(url);

    if (!response.success) {
      throw new Error('Failed to fetch user time logs');
    }

    return response;
  }

  /**
   * Get time logs for a specific date
   */
  static async getTimeLogsByDate(userId: number, date: string): Promise<TimeLogsResponse> {
    try {
      const response = await makeAuthenticatedRequest(`/timelogs/user/${userId}/date/${date}`);
      
      if (!response.success) {
        throw new Error('Failed to fetch time logs for date');
      }

      return response;
    } catch (error: any) {
      console.error('Error getting time logs by date:', error);
      throw error;
    }
  }

  /**
   * Get time log statistics for a user
   */
  static async getTimeLogStats(userId: number, params: GetTimeLogStatsParams = {}): Promise<TimeLogStatsResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.activity_type) queryParams.append('activity_type', params.activity_type);
    if (params.group_by) queryParams.append('group_by', params.group_by);

    const url = `/timelogs/user/${userId}/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await makeAuthenticatedRequest(url);

    if (!response.success) {
      throw new Error('Failed to fetch time log statistics');
    }

    return response;
  }

  /**
   * Get daily summary for a user
   */
  static async getDailySummary(userId: number, params: GetDailySummaryParams = {}): Promise<DailySummaryResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const url = `/timelogs/user/${userId}/daily-summary${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await makeAuthenticatedRequest(url);

    if (!response.success) {
      throw new Error('Failed to fetch daily summary');
    }

    return response;
  }

  /**
   * Update a time log entry
   */
  static async updateTimeLog(timelogId: string, updateData: UpdateTimeLogData): Promise<TimeLog> {
    const response = await makeAuthenticatedRequest(`/timelogs/${timelogId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update time log');
    }

    return response.data;
  }

  /**
   * Delete a time log entry
   */
  static async deleteTimeLog(timelogId: string): Promise<void> {
    const response = await makeAuthenticatedRequest(`/timelogs/${timelogId}`, {
      method: 'DELETE'
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete time log');
    }
  }

  /**
   * Get time logs for a specific activity
   */
  static async getActivityTimeLogs(activityType: 'goal' | 'task' | 'habit' | 'meditation', activityId: string, userId: number): Promise<ActivityTimeLogsResponse> {
    const response = await makeAuthenticatedRequest(`/timelogs/activity/${activityType}/${activityId}?user_id=${userId}`);

    if (!response.success) {
      throw new Error('Failed to fetch activity time logs');
    }

    return response;
  }

  /**
   * Create a time log entry for today
   */
  static async createTodayTimeLog(
    userId: number, 
    activityType: 'goal' | 'task' | 'habit' | 'meditation', 
    activityId: string, 
    activityName: string, 
    durationMinutes: number, 
    startTime: string, 
    endTime: string, 
    description?: string, 
    isManualEntry: boolean = false
  ): Promise<TimeLog> {
    return await this.createTimeLog({
      user_id: userId,
      activity_type: activityType,
      activity_id: activityId,
      activity_name: activityName,
      duration_minutes: durationMinutes,
      start_time: startTime,
      end_time: endTime,
      description,
      is_manual_entry: isManualEntry
    });
  }

  /**
   * Get today's time logs for a user
   */
  static async getTodaysTimeLogs(userId: number): Promise<TimeLog[]> {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const response = await this.getTimeLogsByDate(userId, today);
    return response.data;
  }

  /**
   * Get time logs for the current week
   */
  static async getThisWeeksTimeLogs(userId: number): Promise<TimeLog[]> {
    const today = new Date();
    const startOfWeek = new Date(today);
    const endOfWeek = new Date(today);
    
    // Get Monday as start of week
    const dayOfWeek = today.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startOfWeek.setDate(today.getDate() - daysFromMonday);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    const response = await this.getUserTimeLogs(userId, {
      start_date: startOfWeek.toISOString().split('T')[0],
      end_date: endOfWeek.toISOString().split('T')[0],
      limit: 100
    });

    return response.data;
  }

  /**
   * Get time logs for a specific date range
   */
  static async getTimeLogsInRange(userId: number, startDate: string, endDate: string): Promise<TimeLog[]> {
    const response = await this.getUserTimeLogs(userId, {
      start_date: startDate,
      end_date: endDate,
      limit: 100
    });

    return response.data;
  }

  /**
   * Get total logged time for a date
   */
  static async getTotalLoggedTime(userId: number, date: string): Promise<{ totalMinutes: number; totalHours: number; sessionCount: number }> {
    const response = await this.getTimeLogsByDate(userId, date);
    
    return {
      totalMinutes: response.total_minutes || 0,
      totalHours: response.total_hours || 0,
      sessionCount: response.count || 0
    };
  }

  /**
   * Get total logged time for a date range
   */
  static async getTotalLoggedTimeInRange(userId: number, startDate: string, endDate: string): Promise<{ totalMinutes: number; totalHours: number; sessionCount: number }> {
    const timeLogs = await this.getTimeLogsInRange(userId, startDate, endDate);
    const totalMinutes = timeLogs.reduce((total, log) => total + log.duration_minutes, 0);
    
    return {
      totalMinutes,
      totalHours: Math.round((totalMinutes / 60) * 100) / 100,
      sessionCount: timeLogs.length
    };
  }

  /**
   * Get time logs grouped by activity type
   */
  static async getTimeLogsByActivityType(userId: number, activityType: 'goal' | 'task' | 'habit' | 'meditation', startDate?: string, endDate?: string): Promise<TimeLog[]> {
    const response = await this.getUserTimeLogs(userId, {
      activity_type: activityType,
      start_date: startDate,
      end_date: endDate,
      limit: 100
    });

    return response.data;
  }

  /**
   * Get time logs for a specific activity
   */
  static async getTimeLogsForActivity(userId: number, activityType: 'goal' | 'task' | 'habit' | 'meditation', activityId: string): Promise<TimeLog[]> {
    const response = await this.getActivityTimeLogs(activityType, activityId, userId);
    return response.data;
  }

  /**
   * Get recent time logs (last N days)
   */
  static async getRecentTimeLogs(userId: number, days: number = 7): Promise<TimeLog[]> {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days);

    return await this.getTimeLogsInRange(
      userId, 
      startDate.toISOString().split('T')[0], 
      endDate.toISOString().split('T')[0]
    );
  }

  /**
   * Helper function to format duration for display
   */
  static formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${remainingMinutes}min`;
  }

  /**
   * Helper function to format time for display
   */
  static formatTime(time: string): string {
    const date = new Date(time);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  }

  /**
   * Helper function to format date for display
   */
  static formatDate(date: string): string {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }

  /**
   * Helper function to format date and time for display
   */
  static formatDateTime(dateTime: string): string {
    const dateObj = new Date(dateTime);
    return dateObj.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  }

  /**
   * Helper function to get day name from date
   */
  static getDayName(date: string): string {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dateObj = new Date(date);
    return dayNames[dateObj.getDay()];
  }

  /**
   * Helper function to calculate duration between two timestamps
   */
  static calculateDuration(startTime: string, endTime: string): number {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60)); // minutes
  }

  /**
   * Helper function to create start and end times for a duration
   */
  static createTimeRange(startTime: string, durationMinutes: number): { startTime: string; endTime: string } {
    const start = new Date(startTime);
    const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
    
    return {
      startTime: start.toISOString(),
      endTime: end.toISOString()
    };
  }

  /**
   * Helper function to get current timestamp
   */
  static getCurrentTimestamp(): string {
    return new Date().toISOString();
  }

  /**
   * Helper function to get today's date in YYYY-MM-DD format
   */
  static getTodayDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  /**
   * Helper function to check if a time log is from today
   */
  static isToday(timeLog: TimeLog): boolean {
    const today = this.getTodayDate();
    const logDate = new Date(timeLog.start_time).toISOString().split('T')[0];
    return logDate === today;
  }

  /**
   * Helper function to check if a time log is from this week
   */
  static isThisWeek(timeLog: TimeLog): boolean {
    const today = new Date();
    const logDate = new Date(timeLog.start_time);
    
    // Get Monday of current week
    const dayOfWeek = today.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(today);
    monday.setDate(today.getDate() - daysFromMonday);
    monday.setHours(0, 0, 0, 0);
    
    // Get Sunday of current week
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    return logDate >= monday && logDate <= sunday;
  }

  /**
   * Helper function to get activity type display name
   */
  static getActivityTypeDisplayName(activityType: 'goal' | 'task' | 'habit' | 'meditation'): string {
    const displayNames = {
      goal: 'Goal',
      task: 'Task',
      habit: 'Habit',
      meditation: 'Meditation'
    };
    return displayNames[activityType];
  }

  /**
   * Helper function to get activity type color
   */
  static getActivityTypeColor(activityType: 'goal' | 'task' | 'habit' | 'meditation'): string {
    const colors = {
      goal: '#28a745',
      task: '#007bff',
      habit: '#ffc107',
      meditation: '#9b59b6'
    };
    return colors[activityType];
  }

  /**
   * Helper function to sort time logs by start time
   */
  static sortTimeLogsByStartTime(timeLogs: TimeLog[], ascending: boolean = true): TimeLog[] {
    return [...timeLogs].sort((a, b) => {
      const aTime = new Date(a.start_time).getTime();
      const bTime = new Date(b.start_time).getTime();
      return ascending ? aTime - bTime : bTime - aTime;
    });
  }

  /**
   * Helper function to group time logs by date
   */
  static groupTimeLogsByDate(timeLogs: TimeLog[]): { [date: string]: TimeLog[] } {
    return timeLogs.reduce((groups, log) => {
      const date = new Date(log.start_time).toISOString().split('T')[0];
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(log);
      return groups;
    }, {} as { [date: string]: TimeLog[] });
  }

  /**
   * Helper function to group time logs by activity type
   */
  static groupTimeLogsByActivityType(timeLogs: TimeLog[]): { [activityType: string]: TimeLog[] } {
    return timeLogs.reduce((groups, log) => {
      if (!groups[log.activity_type]) {
        groups[log.activity_type] = [];
      }
      groups[log.activity_type].push(log);
      return groups;
    }, {} as { [activityType: string]: TimeLog[] });
  }

  /**
   * Helper function to calculate total time for a group of time logs
   */
  static calculateTotalTime(timeLogs: TimeLog[]): { totalMinutes: number; totalHours: number } {
    const totalMinutes = timeLogs.reduce((total, log) => total + log.duration_minutes, 0);
    return {
      totalMinutes,
      totalHours: Math.round((totalMinutes / 60) * 100) / 100
    };
  }
}

export default TimeLogsService;
